import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { sentryCaptureError } from 'services/sentry/capture';
import { useAppSelector } from 'store/hooks';
import { cardFieldNameSelector } from 'store/slices/checkout';
import { selectedCardSelector } from 'store/slices/userCard';

import type { CardVerifyResponse } from '../api/reservationClient';
import { checkReservationCardStatus, verifyReservationCard } from '../api/reservationClient';
import { ReservationCardStatus } from '../types';
import { useIsReservationCardBlockVisible } from '../utils/reservationFlags';
import { useReservationVerificationSocket } from './useReservationVerificationSocket';

/** A card the backend knows by id, or a typed PAN it has not given an id yet. */
const getCardKey = (cardId?: number | null, pan?: string): string => (cardId ? `card:${cardId}` : `pan:${pan || ''}`);

/**
 * Drives the inline card-verification block inside the deposit form.
 *
 * Lives here rather than in the form so that `CheckouForm` needs only a hook call and one render:
 * all the state, the upload and the field-readiness logic stay inside the feature.
 *
 * Self-contained by design — no dependency on `CardVerificationController`, the `kycVerification`
 * slice or `checkoutAPI`, all of which belong to the separate `kyc_card_verification` feature.
 */
export const useReservationCardVerification = () => {
  const isVisible = useIsReservationCardBlockVisible();
  const cardFieldName = useAppSelector(cardFieldNameSelector);
  const { selectedCard } = useAppSelector(selectedCardSelector);
  const {
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isCardFieldFocused, setIsCardFieldFocused] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  /**
   * Latest backend answers (`kyc-verify`, `check-status`, socket) by card. The card list is not
   * refetched after them, so a stored card's `kyc_verification_status` stays the snapshot taken
   * when the form opened — these answers win over it, for every card the player switches between.
   */
  const [answeredStatuses, setAnsweredStatuses] = useState<Record<string, ReservationCardStatus>>({});
  /** Card id the backend gave a typed PAN — the same card typed by hand and picked from the list. */
  const [panCardIds, setPanCardIds] = useState<Record<string, number>>({});
  const [verifiedCardId, setVerifiedCardId] = useState<number | null>(selectedCard?.id ?? null);
  const [isChecking, setIsChecking] = useState(false);
  // PAN of the last `check-status` request — one request per typed card, stale answers dropped.
  const lastCheckedPanRef = useRef<string | null>(null);

  const cardFieldValue: string = watch(cardFieldName || '') || '';
  const isStoredCard = !selectedCard?.isNew && !!selectedCard?.id;
  const cardKey = isStoredCard ? getCardKey(selectedCard?.id) : getCardKey(panCardIds[cardFieldValue], cardFieldValue);
  const cardKeyRef = useRef(cardKey);
  cardKeyRef.current = cardKey;

  // A stored card arrives with its status on `GET /v1/user-cards/{amount}`; a freshly typed one has
  // none until the backend answers.
  const knownStatus = (selectedCard?.kyc_verification_status as ReservationCardStatus | null) ?? null;
  const answeredStatus: ReservationCardStatus | undefined = answeredStatuses[cardKey];
  const currentStatus = isUploading
    ? ReservationCardStatus.inProgress
    : (answeredStatus ?? (isStoredCard ? knownStatus : null));

  /**
   * The contract maps a `kyc_verification_status` of 3 *or* 4 to the red state. Status 3 is
   * ambiguous on its own, though — it is also what a stored card that has simply never been
   * confirmed reports — so it only reads as a rejection once the backend has actually answered
   * about this card. Same resolution `CardVerificationController` applies.
   */
  const effectiveStatus =
    currentStatus === ReservationCardStatus.notVerified && (answeredStatus !== undefined || isStoredCard)
      ? ReservationCardStatus.failed
      : currentStatus;

  const setAnswer = useCallback((key: string, status: ReservationCardStatus) => {
    setAnsweredStatuses((prev) => ({ ...prev, [key]: status }));
  }, []);

  // Socket callback for async KYCaid result — the message names its card.
  const handleVerificationResult = useCallback(
    (resultStatus: ReservationCardStatus, cardId: number) => {
      setAnswer(getCardKey(cardId), resultStatus);
      setCurrentFile(null);
    },
    [setAnswer],
  );

  const { connect, disconnect } = useReservationVerificationSocket({
    cardId: verifiedCardId,
    onResult: handleVerificationResult,
  });

  // Shared by the upload (`kyc-verify`) and the new-card check (`kyc-verify/check-status`).
  const applyVerifyResponse = useCallback(
    (response: CardVerifyResponse, pan?: string) => {
      if (pan) {
        setPanCardIds((prev) => ({ ...prev, [pan]: response.card_id }));
      }

      setVerifiedCardId(response.card_id);
      setAnswer(getCardKey(response.card_id), response.kyc_verification_status);

      // If status is final (not IN_PROGRESS), close socket
      if (response.kyc_verification_status !== ReservationCardStatus.inProgress) {
        setCurrentFile(null);
        disconnect();
      }
      // If IN_PROGRESS — socket callback will handle the final result
    },
    [setAnswer, disconnect],
  );

  // No card id comes back with a failure, so it is kept under the card it was requested for.
  const applyVerifyFailure = useCallback(
    (key: string) => {
      setAnswer(key, ReservationCardStatus.failed);
      setCurrentFile(null);
      disconnect();
    },
    [setAnswer, disconnect],
  );

  const handleCheckResponse = useCallback(
    (response: CardVerifyResponse, pan: string) => {
      applyVerifyResponse(response, pan);

      // Unlike the upload, no socket is open yet when the card turns out to be under review.
      if (response.kyc_verification_status === ReservationCardStatus.inProgress) {
        connect();
      }
    },
    [applyVerifyResponse, connect],
  );

  // Connect socket if status is already IN_PROGRESS on mount (state restoration)
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (knownStatus === ReservationCardStatus.inProgress && !hasConnectedRef.current) {
      hasConnectedRef.current = true;
      connect();
    }
  }, [knownStatus, connect]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      disconnect();
    },
    [disconnect],
  );

  useEffect(() => {
    if (!cardFieldName) return undefined;

    const handleFocus = (event: FocusEvent, isFocused: boolean) => {
      const target = event.target as HTMLInputElement;

      if (target?.name === cardFieldName) {
        setIsCardFieldFocused(isFocused);
      }
    };

    const onFocusIn = (event: FocusEvent) => handleFocus(event, true);
    const onFocusOut = (event: FocusEvent) => handleFocus(event, false);

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [cardFieldName]);

  // A freshly typed card is valid once it has a value without errors and the field is left —
  // while focused the PAN validator still accepts a partial number as "potentially valid".
  const isNewCardValid =
    !isStoredCard && !isCardFieldFocused && !!cardFieldName && !errors[cardFieldName] && !!cardFieldValue;
  const isPanKnown = panCardIds[cardFieldValue] !== undefined;

  // A valid new card is checked right away, so a card verified earlier needs no new upload.
  useEffect(() => {
    if (!isVisible || !isNewCardValid || isPanKnown || lastCheckedPanRef.current === cardFieldValue) {
      return;
    }

    const cardPan = cardFieldValue;
    lastCheckedPanRef.current = cardPan;
    setIsChecking(true);

    checkReservationCardStatus(cardPan)
      .then((response) => {
        // The PAN was edited meanwhile — this answer belongs to another card.
        if (lastCheckedPanRef.current !== cardPan) {
          return;
        }

        handleCheckResponse(response, cardPan);
      })
      .catch((error) => {
        sentryCaptureError(error, { loggerId: 'reservation-card-check-status' });

        if (lastCheckedPanRef.current === cardPan) {
          applyVerifyFailure(getCardKey(null, cardPan));
        }
      })
      .finally(() => {
        if (lastCheckedPanRef.current === cardPan) {
          setIsChecking(false);
        }
      });
  }, [isVisible, isNewCardValid, isPanKnown, cardFieldValue, handleCheckResponse, applyVerifyFailure]);

  // A saved card is ready immediately; a freshly typed one once it is valid and its status is checked.
  const isCardReady = (!selectedCard?.isNew && !!selectedCard?.id) || (isNewCardValid && !isChecking);

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;

      if (!files?.length) {
        setCurrentFile(null);
        return;
      }

      const file = files[0];
      const isExistingCard = !!(selectedCard?.id && !selectedCard?.isNew);
      const cardPan = isExistingCard ? undefined : getValues(cardFieldName || '');
      // `inProgress` locks card selection, so the card is the same when the answer arrives.
      const uploadCardKey = cardKeyRef.current;

      setCurrentFile(file);
      setIsUploading(true);

      // The socket filters by this id, so it must be the uploaded card's before the result arrives.
      // A typed card has no id until the response — `null` lets its result through, and the result
      // is stored under the card id the message carries.
      setVerifiedCardId(isExistingCard ? (selectedCard?.id ?? null) : null);

      // Open socket BEFORE the request to catch async KYCaid callback
      connect();

      verifyReservationCard({
        file,
        cardId: isExistingCard ? selectedCard?.id : undefined,
        cardPan,
        saveCard: !!getValues('save_card'),
      })
        .then((response) => applyVerifyResponse(response, cardPan))
        .catch((error) => {
          sentryCaptureError(error, { loggerId: 'reservation-card-block' });
          applyVerifyFailure(uploadCardKey);
        })
        .finally(() => setIsUploading(false));

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [selectedCard, cardFieldName, getValues, connect, applyVerifyResponse, applyVerifyFailure],
  );

  const onReset = useCallback(() => {
    setCurrentFile(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const onOpenPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return {
    isVisible,
    status: effectiveStatus,
    /** True while an upload is in flight — the card must not be edited mid-verification. */
    isPending: effectiveStatus === ReservationCardStatus.inProgress,
    isCardReady,
    inputRef,
    currentFile,
    onFileChange,
    onReset,
    onOpenPicker,
  };
};
